import { MinusOutlined, PlusOutlined, RotateRightOutlined } from '@ant-design/icons';
import { Button, InputNumber, Popover, Slider } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { StyledButton } from '../ImageViewer.style';

import {
  CustomRotationActions,
  CustomRotationContainer,
  CustomRotationInputRow,
} from './Controls.style';

const ROTATION_STEP = 1;
const MAX_ROTATION = 360;

const clampRotation = (degrees: number) => Math.min(MAX_ROTATION, Math.max(0, degrees));

type CustomRotateProps = {
  onChange: (data: { customRotationDeg?: number; customRotationReset?: boolean }) => void;
  value?: number;
  active?: boolean;
  disabled?: boolean;
};

type CustomRotatePanelProps = {
  value: number | null;
  committedValue: number;
  onChange: (degrees: number) => void;
  onInputChange: (degrees: number | null) => void;
  onReset: () => void;
  onClose: () => void;
};

const CustomRotatePanel = ({
  value,
  committedValue,
  onChange,
  onInputChange,
  onReset,
  onClose,
}: CustomRotatePanelProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const displayValue = value ?? 0;

  const handleStep = useCallback(
    (delta: number) => {
      onChange(clampRotation(displayValue + delta));
    },
    [displayValue, onChange]
  );

  return (
    <CustomRotationContainer onClick={event => event.stopPropagation()}>
      <CustomRotationInputRow>
        <Button
          size="small"
          aria-label={t('images.decreaseRotation', 'Decrease rotation')}
          icon={<MinusOutlined />}
          onClick={() => handleStep(-ROTATION_STEP)}
        />
        <InputNumber
          aria-label={t('images.rotationAngle', 'Rotation angle')}
          controls={false}
          data-testid="image-controls-custom-rotation-input"
          formatter={nextValue =>
            nextValue === null || nextValue === undefined ? '' : `${nextValue}°`
          }
          max={MAX_ROTATION}
          min={0}
          onBlur={() => {
            if (value === null) {
              onInputChange(committedValue);
            }
          }}
          onChange={nextValue => {
            if (nextValue === null) {
              onInputChange(null);
              return;
            }

            if (typeof nextValue === 'number' && !Number.isNaN(nextValue)) {
              onChange(clampRotation(nextValue));
            }
          }}
          parser={nextValue => Number((nextValue ?? '').replace('°', ''))}
          precision={1}
          step={ROTATION_STEP}
          value={value}
        />
        <Button
          size="small"
          aria-label={t('images.increaseRotation', 'Increase rotation')}
          icon={<PlusOutlined />}
          onClick={() => handleStep(ROTATION_STEP)}
        />
      </CustomRotationInputRow>

      <div data-testid="image-controls-custom-rotation-slider">
        <Slider
          marks={{
            0: '0°',
            90: '90°',
            180: '180°',
            270: '270°',
            360: '360°',
          }}
          max={MAX_ROTATION}
          min={0}
          onChange={nextValue => {
            if (typeof nextValue === 'number') {
              onChange(nextValue);
            }
          }}
          step={ROTATION_STEP}
          tooltip={{ formatter: nextValue => `${nextValue}°` }}
          styles={{
            track: { background: theme.teal },
            rail: { background: theme.secondaryBackgroundColor },
          }}
          value={displayValue}
        />
      </div>

      <CustomRotationActions>
        <Button
          data-testid="image-controls-custom-rotation-reset"
          disabled={displayValue === 0}
          size="small"
          type="default"
          onClick={onReset}
        >
          {t('components.reset', 'Reset')}
        </Button>
        <Button
          data-testid="image-controls-custom-rotation-close"
          size="small"
          type="primary"
          onClick={onClose}
        >
          {t('common.close', 'Close')}
        </Button>
      </CustomRotationActions>
    </CustomRotationContainer>
  );
};

export const CustomRotate: React.FunctionComponent<CustomRotateProps> = ({
  onChange,
  value = 0,
  active = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [localValue, setLocalValue] = useState<number | null>(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleDegreesChange = useCallback(
    (degrees: number) => {
      const normalizedDegrees = clampRotation(degrees);
      setLocalValue(normalizedDegrees);
      onChange({ customRotationDeg: normalizedDegrees });
    },
    [onChange]
  );

  const handleInputChange = useCallback((degrees: number | null) => {
    setLocalValue(degrees);
  }, []);

  const handleReset = useCallback(() => {
    setLocalValue(0);
    onChange({ customRotationReset: true });
  }, [onChange]);

  return (
    <Popover
      arrow={false}
      content={
        <CustomRotatePanel
          committedValue={value}
          onChange={handleDegreesChange}
          onClose={() => setOpen(false)}
          onInputChange={handleInputChange}
          onReset={handleReset}
          value={localValue}
        />
      }
      onOpenChange={setOpen}
      open={open}
      placement="leftTop"
      trigger="click"
    >
      <StyledButton
        data-testid="image-controls-custom-rotate"
        disabled={disabled}
        title={t('components.rotateToCustomAngle', 'Rotate to custom angle')}
        type={active ? 'primary' : 'default'}
      >
        <RotateRightOutlined />
      </StyledButton>
    </Popover>
  );
};
